/**
 * Source → view model for My Notifications (HS-1443).
 *
 * Permanent: it survives the removal of `src/tmp/`. Every defensive branch is a value a real
 * backend can still send wrong or send *later* — a kind added after this release, a link target we
 * have no route for, an instant that does not parse. The list renders either way rather than
 * throwing inside a render.
 *
 * Locale formatting is deliberately not here: dates travel as `Date` and are formatted by the copy's
 * own `datetime` specifier, so each language decides "18 Feb" or "18 févr." without code.
 */
import type { IconName } from '@smartsash/ui';

import {
  NOTIFICATION_KINDS,
  NOTIFICATION_LINK_TARGETS,
  type NotificationBadge,
  type NotificationItemDto,
  type NotificationKind,
  type NotificationLinkTarget,
  type NotificationLinkView,
  type NotificationMessageView,
  type NotificationPageDto,
  type NotificationPageView,
  type NotificationParamValueDto,
  type NotificationParamView,
  type NotificationTone,
  type NotificationView,
} from './notifications.types';

/** Where every kind's copy lives: `notifications.kinds.<kind>.title|body`. */
export const KIND_COPY_PREFIX = 'notifications.kinds';

interface KindVisual {
  tone: NotificationTone;
  icon: IconName;
}

/**
 * Icon and tone per kind. The five drawn kinds (Figma `2524:161879`) are copied; the rest follow
 * the same rule — red for a block, amber for a deadline, green for good news, neutral for facts.
 */
const KIND_VISUALS: Record<NotificationKind, KindVisual> = {
  sessionFlagged: { tone: 'error', icon: 'alert-triangle' },
  sessionSummary: { tone: 'neutral', icon: 'clock-check' },
  scoreRecalculated: { tone: 'neutral', icon: 'refresh-ccw-05' },
  sashIqBandChanged: { tone: 'success', icon: 'thumbs-up' },
  accessExpiring: { tone: 'warning', icon: 'alert-triangle' },
  accessExpired: { tone: 'error', icon: 'lock-02' },
  extensionApproved: { tone: 'success', icon: 'thumbs-up' },
  extensionRejected: { tone: 'error', icon: 'x-circle' },
  certificationDue: { tone: 'warning', icon: 'certificate-01' },
  certificationExpired: { tone: 'error', icon: 'certificate-01' },
  maintenance: { tone: 'neutral', icon: 'tool-02' },
  announcement: { tone: 'neutral', icon: 'announcement-01' },
  system: { tone: 'neutral', icon: 'info-circle' },
  serviceRequestCreated: { tone: 'neutral', icon: 'inbox-01' },
  serviceRequestWithdrawn: { tone: 'neutral', icon: 'inbox-01' },
  // Completed; a rejection is picked from the `status` param below.
  serviceRequestResolved: { tone: 'success', icon: 'check-circle' },
  // HS-1542: an assignment is a fact for the assignee, not good or bad news.
  serviceRequestAssigned: { tone: 'neutral', icon: 'inbox-01' },
};

/** A band change downwards is the one kind whose look depends on a param. */
const BAND_DROP_VISUAL: KindVisual = { tone: 'warning', icon: 'thumbs-down' };

/** A rejected request is the other outcome of `serviceRequestResolved`. */
const REQUEST_REJECTED_VISUAL: KindVisual = { tone: 'error', icon: 'x-circle' };

/** A resolution whose `status` this release does not know reads as a neutral fact. */
const REQUEST_ANSWERED_VISUAL: KindVisual = { tone: 'neutral', icon: 'inbox-01' };

/** What a kind this release does not know looks like: a neutral fact with no link. */
const UNKNOWN_VISUAL: KindVisual = { tone: 'neutral', icon: 'info-circle' };

/**
 * Params whose value is a closed vocabulary the portal translates, and where its labels live.
 * The band names reuse the Sash IQ ratings, so "Excellent" reads the same on every screen.
 */
const TERM_PARAMS: Record<string, { prefix: string; values: readonly string[] }> = {
  reason: {
    prefix: 'notifications.reasons',
    values: ['correctedData', 'lateSensorReading', 'adminReview'],
  },
  from: {
    prefix: 'dashboard.sashIq.rating',
    values: ['excellent', 'good', 'developing', 'needsAttention'],
  },
  to: {
    prefix: 'dashboard.sashIq.rating',
    values: ['excellent', 'good', 'developing', 'needsAttention'],
  },
  // HS-1548: the service-request type, named as My requests names it.
  requestType: {
    prefix: 'requests.types',
    values: ['AccessExtension', 'AdditionalPermissions', 'Repair', 'Support', 'Message'],
  },
};

/** An instant param is named `…At` (contract §2.2). */
const INSTANT_SUFFIX = 'At';

/** Instant params that predate or ignore that naming rule (HS-1548 `serviceRequestResolved`). */
const INSTANT_PARAMS: readonly string[] = ['extendedUntil'];

/** i18next pluralises on `count`; the contract names the one plural param `days`. */
const PLURAL_ALIASES: Record<string, string> = { days: 'count' };

/** Link targets that cannot navigate without an id. */
const TARGETS_NEEDING_ID: readonly NotificationLinkTarget[] = [
  'session',
  'document',
  'serviceRequest',
];

/** The `status` values of `serviceRequestResolved` that have their own copy. */
const RESOLUTION_STATUSES = ['completed', 'rejected'] as const;
type ResolutionStatus = (typeof RESOLUTION_STATUSES)[number];

function resolutionStatus(
  params: Record<string, NotificationParamValueDto>,
): ResolutionStatus | null {
  const status = params.status;
  return typeof status === 'string' && (RESOLUTION_STATUSES as readonly string[]).includes(status)
    ? (status as ResolutionStatus)
    : null;
}

function present(params: Record<string, NotificationParamValueDto>, name: string): boolean {
  const value = params[name];
  return typeof value === 'string' ? value.trim() !== '' : typeof value === 'number';
}

/**
 * The copy of a resolved request depends on its facts, not only its kind: completed or rejected,
 * whether the access end moved (`extendedUntil` — null, and therefore absent here, when nothing
 * moved), and whether the administrator left a comment. Each combination is its own sentence
 * rather than a concatenation, so each language words it naturally.
 */
function resolvedCopy(params: Record<string, NotificationParamValueDto>): {
  titleKey: string;
  bodyKey: string;
} {
  const base = `${KIND_COPY_PREFIX}.serviceRequestResolved`;
  const status = resolutionStatus(params);
  if (status === null) return { titleKey: `${base}.title`, bodyKey: `${base}.body` };

  const extended = status === 'completed' && present(params, 'extendedUntil');
  const commented = present(params, 'comment');
  const body = extended
    ? commented
      ? 'bodyExtendedComment'
      : 'bodyExtended'
    : commented
      ? 'bodyComment'
      : 'body';
  return { titleKey: `${base}.${status}.title`, bodyKey: `${base}.${status}.${body}` };
}

export function isKnownKind(kind: string): kind is NotificationKind {
  return (NOTIFICATION_KINDS as readonly string[]).includes(kind);
}

function isLinkTarget(target: string): target is NotificationLinkTarget {
  return (NOTIFICATION_LINK_TARGETS as readonly string[]).includes(target);
}

/** `null` for an absent or unparseable instant — never an `Invalid Date` handed to `Intl`. */
export function parseInstant(value: string | null | undefined): Date | null {
  if (value === null || value === undefined || value === '') return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

export function toParamView(name: string, value: NotificationParamValueDto): NotificationParamView {
  if (typeof value === 'number') {
    return Number.isFinite(value) ? { type: 'number', value } : { type: 'text', value: '' };
  }
  const term = TERM_PARAMS[name];
  if (term?.values.includes(value) === true) {
    return { type: 'term', key: `${term.prefix}.${value}` };
  }
  if (name.endsWith(INSTANT_SUFFIX) || INSTANT_PARAMS.includes(name)) {
    const date = parseInstant(value);
    // A malformed instant is shown as sent rather than dropped: a raw string is uglier than a
    // date, but a sentence with a hole in it is wrong.
    if (date !== null) return { type: 'date', value: date };
  }
  return { type: 'text', value };
}

export function toParamsView(
  params: Record<string, NotificationParamValueDto>,
): Record<string, NotificationParamView> {
  const view: Record<string, NotificationParamView> = {};
  for (const [name, value] of Object.entries(params)) {
    view[name] = toParamView(name, value);
    const alias = PLURAL_ALIASES[name];
    if (alias !== undefined && typeof value === 'number') view[alias] = toParamView(alias, value);
  }
  return view;
}

function toMessageView(dto: NotificationItemDto): NotificationMessageView {
  const { message } = dto;
  if (message.type === 'text') {
    return { type: 'text', title: message.title.trim(), body: message.body.trim() };
  }
  // A key the portal has no copy for would render as a raw path; the generic copy says something
  // true instead ("You have a new notification").
  const copy = isKnownKind(message.titleKey) ? message.titleKey : 'unknown';
  if (copy === 'serviceRequestResolved') {
    return { type: 'key', ...resolvedCopy(message.params), params: toParamsView(message.params) };
  }
  return {
    type: 'key',
    titleKey: `${KIND_COPY_PREFIX}.${copy}.title`,
    bodyKey: `${KIND_COPY_PREFIX}.${copy}.body`,
    params: toParamsView(message.params),
  };
}

function toLinkView(dto: NotificationItemDto): NotificationLinkView | null {
  if (dto.link === null || !isLinkTarget(dto.link.target)) return null;
  const id = dto.link.id?.trim() ?? '';
  if (TARGETS_NEEDING_ID.includes(dto.link.target) && id === '') return null;
  // A document link is only as good as the URL the feed carries for it.
  if (dto.link.target === 'document' && dto.document === null) return null;
  return { target: dto.link.target, id: id === '' ? null : id };
}

function visualFor(kind: NotificationKind | 'unknown', dto: NotificationItemDto): KindVisual {
  if (kind === 'unknown') return UNKNOWN_VISUAL;
  if (kind === 'serviceRequestResolved' && dto.message.type === 'key') {
    const status = resolutionStatus(dto.message.params);
    if (status === 'rejected') return REQUEST_REJECTED_VISUAL;
    if (status === null) return REQUEST_ANSWERED_VISUAL;
  }
  if (
    kind === 'sashIqBandChanged' &&
    dto.message.type === 'key' &&
    dto.message.params.direction === 'down'
  ) {
    return BAND_DROP_VISUAL;
  }
  return KIND_VISUALS[kind];
}

/** Severity in words (Figma: "Action needed" / "Info"). */
export function badgeFor(severity: string): NotificationBadge {
  return severity === 'warning' || severity === 'critical' ? 'actionNeeded' : 'info';
}

/**
 * One row. `null` for a row that cannot be placed on the timeline at all (no parseable
 * `createdAt`): grouping it under "Earlier" would be a guess dressed as a fact.
 */
export function toNotificationView(dto: NotificationItemDto): NotificationView | null {
  const createdAt = parseInstant(dto.createdAt);
  if (createdAt === null) return null;

  const kind = isKnownKind(dto.kind) ? dto.kind : 'unknown';
  const visual = visualFor(kind, dto);
  // Which known kinds an app declines to link is its own policy (`kindsWithoutLink`), applied
  // when the row renders; the view keeps every link the feed sent.
  const link = kind === 'unknown' ? null : toLinkView(dto);

  return {
    id: dto.id,
    kind,
    tone: visual.tone,
    icon: visual.icon,
    badge: badgeFor(dto.severity),
    createdAt,
    unread: dto.readAt === null,
    message: toMessageView(dto),
    link,
    documentUrl: link?.target === 'document' ? (dto.document?.url ?? null) : null,
  };
}

export function toNotificationPageView(dto: NotificationPageDto): NotificationPageView {
  return {
    items: dto.items
      .map(toNotificationView)
      .filter((item): item is NotificationView => item !== null),
    nextCursor: dto.nextCursor === '' ? null : dto.nextCursor,
    unreadCount: Math.max(0, dto.unreadCount),
    totalCount: Math.max(0, dto.totalCount),
  };
}
